import { useLayoutEffect } from 'react';

const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
]);

export const isEventInsideExcluded = (event, excludeSelector) => {
  const target = event.target;
  return target instanceof Element && target.closest(excludeSelector) !== null;
};

// Locks the background page's scroll (wheel, trackpad, keyboard, scrollbar drag)
// while `active` is true, without ever intercepting input inside `excludeSelector`.
// Restores the exact scroll position and prior inline styles on deactivate.
export const useScrollLock = (active, excludeSelector) => {
  useLayoutEffect(() => {
    if (!active) return undefined;

    const scrollY = window.scrollY;
    const prevBodyStyle = {
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevOverscrollBehavior = document.documentElement.style.overscrollBehavior;

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.overscrollBehavior = 'contain';

    const onWheelOrTouch = (e) => {
      if (isEventInsideExcluded(e, excludeSelector)) return;
      e.preventDefault();
    };

    const onKeyDown = (e) => {
      if (!SCROLL_KEYS.has(e.key)) return;
      if (isEventInsideExcluded(e, excludeSelector)) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      e.preventDefault();
    };

    document.addEventListener('wheel', onWheelOrTouch, { passive: false });
    document.addEventListener('touchmove', onWheelOrTouch, { passive: false });
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('wheel', onWheelOrTouch);
      document.removeEventListener('touchmove', onWheelOrTouch);
      document.removeEventListener('keydown', onKeyDown);

      document.body.style.position = prevBodyStyle.position;
      document.body.style.top = prevBodyStyle.top;
      document.body.style.width = prevBodyStyle.width;
      document.body.style.overflow = prevBodyStyle.overflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.overscrollBehavior = prevOverscrollBehavior;

      window.scrollTo(0, scrollY);
    };
  }, [active, excludeSelector]);
};

export default useScrollLock;
